import { toast } from 'react-toastify';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import EditProfileModal from './EditProfileModal';
import ChangePasswordModal from './ChangePasswordModal';

interface HeaderProps {
  onBack?: () => void;
  backLabel?: string;
}

const Header: React.FC<HeaderProps> = ({ onBack, backLabel = 'Back' }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { selectedWorkspace } = useWorkspace();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEditProfile = () => {
    setShowUserMenu(false);
    setShowEditProfileModal(true);
  };

  const handleChangePassword = () => {
    setShowUserMenu(false);
    setShowChangePasswordModal(true);
  };

  return (
    <header className="app-header">
      <div className="app-header-left">
        {onBack && (
          <button className="app-header-back-btn" onClick={onBack}>
            <Home size={18} />
          </button>
        )}
        <img src="/logo-light.png" alt="EnHomeBuilder" style={{ height: '32px', marginRight: '16px' }} />
        <h1 className="app-header-workspace-name">
          {selectedWorkspace?.name || 'Workspace'}
        </h1>
      </div>
      <div className="app-header-right">
        <div className="app-header-user" ref={menuRef}>
          <button
            className="app-header-user-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <span className="app-header-user-name">{user?.name}</span>
            <svg
              className={`app-header-caret ${showUserMenu ? 'open' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {showUserMenu && (
            <div className="app-header-user-menu">
              <button
                className="app-header-user-menu-item"
                onClick={handleEditProfile}
              >
                Edit Profile
              </button>
              <button
                className="app-header-user-menu-item"
                onClick={handleChangePassword}
              >
                Change Password
              </button>
              <div className="app-header-user-menu-divider" />
              <button
                className="app-header-user-menu-item"
                onClick={handleLogout}
              >
                Log Off
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <EditProfileModal
        isOpen={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
      />
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </header>
  );
};

export default Header;
